import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import SpecificExercises from './SpecificExercises';
import InteractiveActivities from './InteractiveActivities';
import ActivityCatalogView from './ActivityCatalogView';
import HeroPathSection from './HeroPathSection';
import HeroNarrateButton from './HeroNarrateButton';
import Games from './Games';
import HeroCompanion from './HeroCompanion';
import { TOTAL_ACTIVITIES, ACTIVITY_CATALOG } from '../data/activityCatalog';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { HEROES, HERO_ORDER, heroForActivity } from '../data/heroes';
import useHeroNarrator from '../hooks/useHeroNarrator';
import {
  Wind, Activity, Brain, Palette, Flame, MessageCircle,
  ChevronLeft, ChevronRight, X, Sparkles, CheckCircle2, Crown, Mic, Pause, Play,
} from 'lucide-react';

const TYPE_META = {
  breathe: { Icon: Wind, color: 'from-blue-400 to-cyan-500' },
  move: { Icon: Activity, color: 'from-green-400 to-blue-500' },
  reflect: { Icon: Brain, color: 'from-purple-400 to-pink-500' },
  create: { Icon: Palette, color: 'from-orange-400 to-red-500' },
  affirm: { Icon: Flame, color: 'from-yellow-400 to-orange-500' },
  talk: { Icon: MessageCircle, color: 'from-sky-400 to-blue-500' },
};

const HERO_HEAD_ICONS = { queen: Crown, victor: Mic, cam: Wind };

const Activities = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const location = useLocation();
  const queryHero = new URLSearchParams(location.search).get('hero');
  const [activeTab, setActiveTab] = useState('heroes');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // legacy generic activity-by-type
  const [activeActivity, setActiveActivity] = useState(null);
  // hero-led activity (from catalog) — { hero, activity }
  const [activeHeroLed, setActiveHeroLed] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepSecondsLeft, setStepSecondsLeft] = useState(0);
  const [paused, setPaused] = useState(false);
  const [expandedHeros, setExpandedHeros] = useState(() => new Set());
  const toggleHero = (hk) => {
    setExpandedHeros((prev) => {
      const nx = new Set(prev);
      if (nx.has(hk)) nx.delete(hk); else nx.add(hk);
      return nx;
    });
  };
  const narrator = useHeroNarrator();
  const lastNarratedRef = useRef(null);

  const [sessionData, setSessionData] = useState({
    duration_minutes: '', notes: '', mood_before: '', mood_after: '',
  });

  useEffect(() => {
    if (queryHero && HEROES[queryHero]) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="hero-path-${queryHero}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-4', HEROES[queryHero].ring);
          setTimeout(() => el.classList.remove('ring-4', HEROES[queryHero].ring), 2500);
        }
      }, 400);
    }
  }, [queryHero]);

  const buildActivity = (key) => ({
    title: t(`activities.types.${key}.title`),
    description: t(`activities.types.${key}.description`),
    instructions: t(`activities.types.${key}.instructions`, { returnObjects: true }) || [],
    benefits: t(`activities.types.${key}.benefits`, { returnObjects: true }) || [],
    ...TYPE_META[key],
  });
  const activities = Object.keys(TYPE_META).reduce((acc, k) => {
    acc[k] = buildActivity(k); return acc;
  }, {});

  const moods = {
    very_low: { label: t('activities.moodVeryLow') },
    low: { label: t('activities.moodLow') },
    neutral: { label: t('activities.moodNeutral') },
    good: { label: t('activities.moodGood') },
    excellent: { label: t('activities.moodExcellent') },
  };

  useEffect(() => { fetchSessions(); }, []);
  const fetchSessions = async () => {
    try { const r = await axios.get('/activities'); setSessions(r.data); }
    catch { toast.error('Failed to load activity sessions'); }
    finally { setLoading(false); }
  };

  const startActivity = (activityType) => {
    setActiveActivity(activityType);
    setStepIndex(0);
    setSessionData({ duration_minutes: '', notes: '', mood_before: '', mood_after: '' });
  };

  const startHeroLed = (heroKey, activityObj) => {
    setActiveHeroLed({ heroKey, activity: activityObj });
    setStepIndex(0);
    setSessionData({ duration_minutes: '', notes: '', mood_before: '', mood_after: '' });
  };

  const closeActive = () => {
    narrator.stop();
    setActiveActivity(null);
    setActiveHeroLed(null);
    setStepIndex(0);
    setStepSecondsLeft(0);
    setPaused(false);
    stepInstructionsRef.current = [];
    lastNarratedRef.current = null;
  };

  const completeActivity = async () => {
    try {
      const activityType =
        activeHeroLed ? activeHeroLed.activity.base : activeActivity;
      const duration = activeHeroLed?.activity?.duration ||
        (sessionData.duration_minutes ? parseInt(sessionData.duration_minutes) : null);
      const sessionPayload = {
        activity_type: activityType,
        duration_minutes: duration,
        notes: sessionData.notes,
        mood_before: sessionData.mood_before || null,
        mood_after: sessionData.mood_after || null,
      };
      const response = await axios.post('/activities', sessionPayload);
      setSessions([response.data, ...sessions]);
      toast.success(t('heroLed.youDidIt'));
      closeActive();
    } catch { toast.error('Failed to save activity session'); }
  };

  const handleSpecificExerciseComplete = async (data) => {
    try {
      const r = await axios.post('/activities', {
        activity_type: 'reflect', duration_minutes: 5,
        notes: data.notes, mood_after: 'good',
      });
      setSessions([r.data, ...sessions]);
      toast.success(`${t('heroLed.youDidIt')} ${data.title}`);
    } catch { toast.error('Failed to save exercise session'); }
  };
  const handleInteractiveActivityComplete = async (data) => {
    try {
      const r = await axios.post('/activities', {
        activity_type: 'create', duration_minutes: 10,
        notes: `Completed: ${data.title}`, mood_after: 'good',
      });
      setSessions([r.data, ...sessions]);
      toast.success(`${t('heroLed.youDidIt')} ${data.title}`);
    } catch { toast.error('Failed to save activity session'); }
  };

  const formatDate = (s) =>
    new Date(s).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US',
      { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Compute a kid-friendly per-step duration (seconds). Longer for breathwork steps,
  // shorter for quick instructions. Bounded 14–35s.
  const stepDurationFor = (text) => {
    if (!text) return 18;
    const len = text.split(/\s+/).length;
    const breathy = /\b(breath|inhale|exhale|breathe|respira|inhala|exhala|hold|aguanta|notice|nota)\b/i.test(text);
    const base = Math.round(len * 1.5) + (breathy ? 12 : 4);
    return Math.max(14, Math.min(35, base));
  };

  // Reset & start the per-step countdown whenever the step changes (and we're in the hero-led flow).
  const activeHeroLedRef = useRef(null);
  const stepInstructionsRef = useRef([]);
  useEffect(() => {
    if (!activeActivity && !activeHeroLed) return;
    // recompute current instructions list (mirror of render-time logic) so the timer doesn't drift
    let instructions = stepInstructionsRef.current;
    if (instructions.length === 0) return;
    setStepSecondsLeft(stepDurationFor(instructions[stepIndex] || ''));
  }, [stepIndex, activeActivity, activeHeroLed]);

  useEffect(() => {
    if ((!activeActivity && !activeHeroLed) || paused) return;
    if (stepSecondsLeft <= 0) return;
    const t = setInterval(() => setStepSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [activeActivity, activeHeroLed, paused, stepSecondsLeft]);

  // Auto-advance when timer hits zero (only if not on the last step).
  useEffect(() => {
    if ((!activeActivity && !activeHeroLed) || paused) return;
    const list = stepInstructionsRef.current;
    if (!list.length) return;
    if (stepSecondsLeft === 0 && stepIndex < list.length - 1) {
      const id = setTimeout(() => setStepIndex((i) => Math.min(list.length - 1, i + 1)), 500);
      return () => clearTimeout(id);
    }
  }, [stepSecondsLeft, stepIndex, activeActivity, activeHeroLed, paused]);

  // ============================================================
  // HERO-LED ACTIVE FLOW (Calm-style, deep blue, hero is the guide)
  // ============================================================
  const isActive = !!activeActivity || !!activeHeroLed;
  if (isActive) {
    const heroKey = activeHeroLed
      ? activeHeroLed.heroKey
      : (heroForActivity(activeActivity)?.key || 'cam');
    const hero = HEROES[heroKey];
    const tr = t(`heroes.${heroKey}`, { returnObjects: true });

    let title, description, instructions, benefits;
    if (activeHeroLed) {
      const a = activeHeroLed.activity;
      title = a.title[lang]; description = a.desc[lang];
      instructions = a.instructions[lang] || [];
      benefits = [];
    } else {
      const a = activities[activeActivity];
      title = a.title; description = a.description;
      instructions = a.instructions; benefits = a.benefits;
    }

    const totalSteps = instructions.length;
    const isLast = stepIndex === totalSteps - 1;
    const currentStep = instructions[stepIndex] || '';
    // Sync ref so the global timer effect sees the current step list.
    stepInstructionsRef.current = instructions;
    activeHeroLedRef.current = activeHeroLed;
    // First-mount seed: ensure timer starts immediately on step 0 if it's still 0.
    if (stepSecondsLeft === 0 && stepIndex === 0 && currentStep && !paused) {
      // setState in render is OK only via effect; defer with microtask
      Promise.resolve().then(() => setStepSecondsLeft(stepDurationFor(currentStep)));
    }
    const stepTotal = stepDurationFor(currentStep) || 1;
    const stepProgress = ((stepTotal - stepSecondsLeft) / stepTotal) * 100;

    // Auto-narrate every new step (avoid duplicate triggers)
    const narrateKey = `${heroKey}-${title}-${stepIndex}`;
    if (lastNarratedRef.current !== narrateKey && currentStep) {
      lastNarratedRef.current = narrateKey;
      // fire and forget — paraphrase via LLM so the hero sounds human + varies each session
      narrator.play(currentStep, heroKey, true, lang, true, { paraphraseStyle: "activity" });
    }

    const HeadIcon = HERO_HEAD_ICONS[heroKey] || Sparkles;

    return (
      <div className="bs-shell" data-testid={`hero-led-${activeHeroLed ? activeHeroLed.activity.id : activeActivity}`}>
        <div className="bs-stars" />
        <div className="bs-aurora" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <span className="bs-chip">
              <HeadIcon className="w-4 h-4 text-amber-300" />
              {tr.pathTitle}
            </span>
            <button
              onClick={closeActive}
              className="text-blue-100/80 hover:text-white inline-flex items-center gap-1 text-sm"
              data-testid="cancel-activity-btn"
            >
              <X className="w-4 h-4" /> {t('heroLed.closeNarration')}
            </button>
          </div>

          {/* HERO + SPEECH BUBBLE */}
          <div className="grid sm:grid-cols-[200px_1fr] gap-6 items-start mb-8">
            <div className="flex justify-center sm:justify-start">
              <div className={`relative hero-avatar w-44 h-52 ${hero.imageBg} float-animation`}>
                <img src={hero.image} alt={tr.name} />
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-300 rounded-full border-2 border-blue-950 animate-pulse" />
              </div>
            </div>
            <div className="hero-speech" data-testid="hero-speech-bubble">
              <p className="text-xs uppercase font-bold tracking-widest text-blue-700 mb-1">
                {tr.name} {lang === 'es' ? 'dice' : 'says'}…
              </p>
              <p className="text-lg sm:text-xl font-extrabold leading-snug mb-2">{title}</p>
              <p className="text-sm text-gray-700">{description}</p>
              <p className="mt-3 text-base font-semibold text-blue-950">
                "{narrator.lastSpoken || currentStep}"
              </p>
              <div className="mt-3">
                <HeroNarrateButton
                  hero={heroKey}
                  text={currentStep}
                  label={t('heroLed.listenAlong', { hero: tr.name })}
                  guided={true}
                />
              </div>
            </div>
          </div>

          {/* STEP PROGRESS */}
          <Card className="bs-glass border-0 p-6 mb-6" data-testid="hero-led-stepper">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="bs-amber text-xs font-bold uppercase tracking-widest">
                {t('activities.instructions')} · {stepIndex + 1}/{totalSteps}
              </p>
              <div className="flex items-center gap-2">
                <span className="bs-chip" data-testid="hero-led-timer">
                  {Math.max(0, stepSecondsLeft)}s
                </span>
                <button
                  onClick={() => setPaused((p) => !p)}
                  className="bs-chip"
                  data-testid="hero-led-pause-toggle"
                  aria-pressed={paused}
                  aria-label={paused ? 'resume timer' : 'pause timer'}
                >
                  {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => { setStepIndex(Math.max(0, stepIndex - 1)); }}
                  disabled={stepIndex === 0}
                  className="bs-chip disabled:opacity-40"
                  data-testid="hero-led-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setStepIndex(Math.min(totalSteps - 1, stepIndex + 1)); }}
                  disabled={isLast}
                  className="bs-chip disabled:opacity-40"
                  data-testid="hero-led-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Per-step countdown bar */}
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-4" aria-hidden="true">
              <div
                className="h-full bg-gradient-to-r from-amber-300 to-blue-400 transition-all duration-700"
                style={{ width: `${stepProgress}%` }}
                data-testid="hero-led-step-progress"
              />
            </div>
            <div className="space-y-2.5">
              {instructions.map((step, i) => (
                <div
                  key={i}
                  className={`bs-step ${i === stepIndex ? 'active' : ''}`}
                  onClick={() => setStepIndex(i)}
                  data-testid={`hero-led-step-${i}`}
                  role="button"
                >
                  <div className="num">{i + 1}</div>
                  <p className="text-sm leading-relaxed flex-1">{step}</p>
                  {i < stepIndex && <CheckCircle2 className="w-5 h-5 text-amber-300 flex-shrink-0" />}
                </div>
              ))}
            </div>
            {benefits && benefits.length > 0 && (
              <div className="mt-5">
                <p className="bs-muted text-xs font-bold uppercase tracking-widest mb-2">{t('activities.benefits')}</p>
                <div className="flex flex-wrap gap-2">
                  {benefits.map((b, i) => (
                    <Badge key={i} className="bg-white/10 text-blue-100 border-white/20">{b}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* COMPLETE FORM */}
          <Card className="bs-glass border-0 p-6">
            <h2 className="bs-title text-lg font-extrabold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-300" /> {t('activities.completeSession')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs text-blue-100/80 font-semibold uppercase tracking-widest mb-2">
                  {t('activities.feelBefore')}
                </label>
                <Select value={sessionData.mood_before} onValueChange={(v) => setSessionData({ ...sessionData, mood_before: v })}>
                  <SelectTrigger data-testid="mood-before-select" className="bg-white/10 border-white/20 text-white"><SelectValue placeholder={t('activities.selectMood')} /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(moods).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-blue-100/80 font-semibold uppercase tracking-widest mb-2">
                  {t('activities.feelAfter')}
                </label>
                <Select value={sessionData.mood_after} onValueChange={(v) => setSessionData({ ...sessionData, mood_after: v })}>
                  <SelectTrigger data-testid="mood-after-select" className="bg-white/10 border-white/20 text-white"><SelectValue placeholder={t('activities.selectMood')} /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(moods).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs text-blue-100/80 font-semibold uppercase tracking-widest mb-2">
                {t('activities.notes')}
              </label>
              <Textarea
                className="bg-white/10 border-white/20 text-white placeholder:text-blue-100/60"
                placeholder={t('activities.notesHint')}
                value={sessionData.notes}
                onChange={(e) => setSessionData({ ...sessionData, notes: e.target.value })}
                data-testid="session-notes-textarea"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={completeActivity}
                className="bs-cta flex-1 justify-center"
                data-testid="complete-activity-btn"
              >
                {t('heroLed.completeAndSave')}
                <Sparkles className="w-4 h-4" />
              </button>
              <button
                onClick={closeActive}
                className="bs-chip justify-center px-6 py-3"
              >
                {t('activities.cancel')}
              </button>
            </div>
            <p className="bs-muted text-xs mt-4 italic text-center">
              {t('heroLed.heroProud', { hero: tr.name })}
            </p>
          </Card>
        </div>
      </div>
    );
  }

  // ============================================================
  // ACTIVITIES HUB (Calm-style page)
  // ============================================================
  return (
    <div className="bs-shell" data-testid="activities-page">
      <div className="bs-stars" />
      <div className="bs-aurora" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <p className="bs-amber font-semibold uppercase tracking-widest text-xs mb-2">
            <Sparkles className="inline w-3.5 h-3.5 mr-1 -mt-0.5" /> {t('heroLed.featured')}
          </p>
          <h1 className="bs-title text-3xl sm:text-4xl font-extrabold mb-1">
            {t('activities.pageTitle')}
          </h1>
          <p className="bs-muted text-sm">{t('activities.pageSubtitle')}</p>
        </div>

        {/* Always-on hero companion at top */}
        <div className="mb-6">
          <HeroCompanion variant="footer" />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="-mx-4 sm:mx-0 mb-6 overflow-x-auto no-scrollbar">
            <TabsList className="inline-flex w-max sm:w-full sm:flex-wrap h-auto bg-white/5 border border-white/10 backdrop-blur p-1 mx-4 sm:mx-0">
              <TabsTrigger value="heroes" data-testid="heroes-tab" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('heroes.chooseYourHero')}</TabsTrigger>
              <TabsTrigger value="games" data-testid="games-tab" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{lang === 'es' ? 'Juegos' : 'Games'}</TabsTrigger>
              <TabsTrigger value="catalog" data-testid="catalog-tab" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{lang === 'es' ? 'Catálogo' : 'Catalogue'} ({TOTAL_ACTIVITIES}+)</TabsTrigger>
              <TabsTrigger value="featured" data-testid="featured-tab" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('activities.featuredTab')}</TabsTrigger>
              <TabsTrigger value="interactive" data-testid="interactive-tab" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('activities.interactiveTab')}</TabsTrigger>
              <TabsTrigger value="classic" data-testid="classic-tab" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('activities.classicTab')}</TabsTrigger>
              <TabsTrigger value="history" data-testid="history-tab" className="whitespace-nowrap data-[state=active]:bg-white/15 data-[state=active]:text-white text-blue-100/80">{t('activities.historyTab')}</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="games">
            <Games />
          </TabsContent>

          <TabsContent value="catalog">
            <ActivityCatalogView onStartHeroLed={startHeroLed} onStart={(base) => { startActivity(base); }} />
          </TabsContent>

          <TabsContent value="heroes">
            <div className="space-y-5">
              {HERO_ORDER.map((hk) => (
                <HeroPathSection
                  key={hk}
                  heroKey={hk}
                  expanded={expandedHeros.has(hk)}
                  onToggle={() => toggleHero(hk)}
                  onStartHeroLed={startHeroLed}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="featured">
            <Card className="bs-glass border-0 p-6">
              <SpecificExercises onComplete={handleSpecificExerciseComplete} />
            </Card>
          </TabsContent>

          <TabsContent value="interactive">
            <Card className="bs-glass border-0 p-6">
              <InteractiveActivities onActivityComplete={handleInteractiveActivityComplete} />
            </Card>
          </TabsContent>

          <TabsContent value="classic">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {Object.entries(activities).map(([key, activity]) => {
                const hero = heroForActivity(key);
                const TypeIcon = activity.Icon;
                return (
                  <Card key={key} className="bs-glass border-0 p-5" data-testid={`activity-card-${key}`}>
                    <div className="flex items-start gap-3 mb-3">
                      {hero ? (
                        <div className={`hero-avatar w-14 h-16 ${hero.imageBg}`}>
                          <img src={hero.image} alt="" />
                        </div>
                      ) : (
                        <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${activity.color} flex items-center justify-center text-white`}>
                          <TypeIcon className="w-6 h-6" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="bs-title font-extrabold text-lg leading-tight">{activity.title}</h3>
                        {hero && (
                          <Badge className="bg-white/10 text-amber-300 border-amber-300/30 mt-1 text-xs" data-testid={`activity-hero-badge-${key}`}>
                            {t(`heroes.${hero.key}.pathTitle`)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="bs-muted text-sm mb-4">{activity.description}</p>
                    <button onClick={() => startActivity(key)} className="bs-cta w-full justify-center" data-testid={`start-${key}-btn`}>
                      {t('activities.startActivity')}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="space-y-4">
              <h2 className="bs-title text-xl font-extrabold">
                {t('activities.recentSessions')} ({sessions.length})
              </h2>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => <Card key={i} className="bs-glass border-0 p-6 h-24" />)}
                </div>
              ) : sessions.length === 0 ? (
                <Card className="bs-glass border-0 p-8 text-center" data-testid="no-sessions-message">
                  <Sparkles className="w-10 h-10 mx-auto mb-3 text-amber-300" />
                  <h3 className="bs-title text-lg font-bold mb-2">{t('activities.noSessions')}</h3>
                  <p className="bs-muted">{t('activities.noSessionsDesc')}</p>
                </Card>
              ) : (
                sessions.map((session, index) => {
                  const meta = activities[session.activity_type];
                  const SIcon = meta?.Icon || Sparkles;
                  return (
                    <Card key={session.id} className="bs-glass border-0 p-5" data-testid={`session-${index}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${meta?.color || 'from-blue-400 to-blue-600'} flex items-center justify-center text-white`}>
                            <SIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="bs-title font-semibold">{meta?.title || session.activity_type}</h3>
                            <p className="bs-muted text-xs">
                              {formatDate(session.created_at)}
                              {session.duration_minutes && ` · ${session.duration_minutes}m`}
                            </p>
                          </div>
                        </div>
                        {session.mood_after && (
                          <Badge className="bg-white/10 text-amber-300 border-amber-300/30">
                            {moods[session.mood_after]?.label}
                          </Badge>
                        )}
                      </div>
                      {session.notes && (
                        <p className="mt-3 text-sm bs-muted italic">"{session.notes}"</p>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Activities;
